import * as v from 'valibot';

import type { ResponseResult } from '#shared/api';

import type {
  NopEmployeeReservationNode,
  NopEmployeeReservationsBody,
  NopResourcesDateRangeRead,
  NopResourcesRead,
  NopResourcesStatus,
} from '../model/types';

/**
 * Adapts a NOP resources read onto the repo's response envelope.
 *
 * `/api/nop/*` answers with `{ status, cached, data, meta }` rather than the
 * `{ data, dataGeneratedAt, errors, … }` every other endpoint here uses, so this
 * translates once at the boundary and the existing
 * `validateAndTransformResponse` / `usePollingQuery` machinery works unchanged.
 *
 * `status` deliberately does NOT ride along inside the envelope: it is not
 * validation state, and folding it in would let a consumer read `data` without
 * seeing it. It is returned separately by the api's `transformResponse`.
 */
export const asResponseResult = <T>(
  read: NopResourcesRead<T[]> | undefined,
  /** What an unwritten or empty entry looks like as data. */
  fallback: T[] = [],
): ResponseResult<T[]> => ({
  data: read?.data ?? fallback,
  errors: null,
  expires: null,
  dataGeneratedAt: read?.meta?.generatedAt ?? null,
  errorsGeneratedAt: null,
});

/**
 * The read's status, defaulting to `absent` for anything unrecognised.
 *
 * Defaulting the OTHER way would be the dangerous choice: a malformed body
 * reported as `present` with no data reads as "this hub genuinely has no
 * consoles", which is the exact conflation CODE-012 section 6 removed from the
 * BE. `absent` says "we do not know", which is true.
 */
export const readStatus = (
  read: { status?: unknown } | undefined,
): NopResourcesStatus =>
  read?.status === 'present' || read?.status === 'empty'
    ? read.status
    : 'absent';

/** One date out of a range read, with that date's own status kept intact. */
export interface NopResourcesDay<T> {
  date: string;
  status: NopResourcesStatus;
  data: T | null;
}

/**
 * Flattens a date-range read into one entry per date, in date order.
 *
 * The BE returns `days` as a map and deliberately does not merge the range,
 * because each date carries its own status. This preserves that — a caller sees
 * which dates were served and which were never swept — while giving the array
 * shape `validateAndTransformResponse` needs.
 *
 * Sorted because `Object.entries` order on a string-keyed map is insertion order,
 * and the axis reads these as a sequence. `YYYY-MM-DD` sorts correctly as text.
 */
export const asDateRangeDays = <T>(
  read: NopResourcesDateRangeRead<T> | undefined,
): NopResourcesDay<T>[] =>
  Object.entries<NopResourcesRead<T> | undefined>(read?.days ?? {})
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, day]) => ({
      date,
      status: readStatus(day),
      data: day?.data ?? null,
    }));

/**
 * Flattens the employee-keyed map into one entry per node.
 *
 * `validateAndTransformResponse` drops items by array index, so a flat array is
 * what lets one malformed node drop alone rather than taking its employee's
 * whole history with it. An employee whose value is not an array contributes no
 * nodes and leaves the others intact.
 */
export const asEmployeeReservationNodes = (
  body: NopEmployeeReservationsBody | undefined,
): NopEmployeeReservationNode[] =>
  Object.entries(body?.employees ?? {}).flatMap(([employeeId, nodes]) =>
    (Array.isArray(nodes) ? nodes : []).map(node => ({ ...node, employeeId })),
  );

/** The wire envelope, validated only as far as the fields this layer reads. */
export const nopResourcesMetaSchema = v.nullable(
  v.object({
    generatedAt: v.string('NopResourcesMeta.generatedAt'),
    from: v.optional(v.nullable(v.string())),
    to: v.optional(v.nullable(v.string())),
  }),
);
